import { createElement, type Key } from 'react';
import type { TableColumnType } from 'antd';
import type { CompareFn, FilterValue, SortOrder } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { CRM_COPY } from './crm/copy';
import { SearchFilterDropdown } from './SearchFilterDropdown';
import { BLANK, GRID_COPY } from './leadGridCopy';
import type { BatchLead, LeadTag } from './types';

export { BLANK, GRID_COPY };

/**
 * Sort and filter for the Batch leads grid (My Leads in the portal, Batch in
 * the console — one shared table). Everything here runs in the browser over
 * the rows already loaded (port plan D7): there are no server sort or filter
 * params, and the grid says so with its loaded-rows cue whenever a sort or a
 * filter is active and more pages remain.
 *
 * Two filter kinds:
 *   - pick list: the distinct values present in the loaded rows, plus
 *     "(Blank)", with a search box once the list is long;
 *   - search: "Contains…" plus "Only blank", for free text (Name, Email,
 *     Phone and the Notes column's latest note).
 * Filters on different columns AND together; the values ticked inside one
 * column OR together (antd's own semantics, mirrored by `filterLeads`).
 *
 * The predicates live here, keyed by column key, rather than inline in the
 * column definitions, so the table can compute the rows a filter leaves on
 * screen with exactly the functions antd filters with — the selection guard
 * (a filter must never hide a ticked row from a bulk action) depends on that.
 */

/** antd's filter state, as `Table.onChange` reports it: column key → values. */
export type LeadFilters = Record<string, FilterValue | null>;

/** The grid's sort, as antd reports it. `null` = no column sorted. */
export interface LeadSort {
  columnKey: string;
  order: Exclude<SortOrder, null>;
}

/** Captured, newest first — the grid's long-standing default order. */
export const DEFAULT_LEAD_SORT: LeadSort = { columnKey: 'createdAt', order: 'descend' };

type CellGetter = (row: BatchLead) => string | string[] | null | undefined;
type OnFilter = (value: Key | boolean, row: BatchLead) => boolean;
/** A column's filter predicate, as antd's `onFilter` takes it. */
export type LeadFilterPredicate = OnFilter;

/** Trimmed, non-empty values of a cell. An empty array means "blank". */
export function cellValues(get: CellGetter, row: BatchLead): string[] {
  const v = get(row);
  return (Array.isArray(v) ? v : [v]).map((x) => (x ?? '').trim()).filter(Boolean);
}

// ── Day grouping (Captured) ────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * `YYYY-MM-DD` of a timestamp in the VIEWER's local zone — the same zone the
 * Captured cell shows its time in (`formatCapturedTime`). Not
 * `iso.slice(0, 10)`: that is the UTC date, which puts every evening scan
 * west of UTC on the next day.
 */
export function localDayKey(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "Fri, May 1" (or "Fri, May 1, 2026" when the list spans years). */
export function dayLabel(dayKey: string, withYear: boolean): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' as const } : {}),
  });
}

// ── Tags ───────────────────────────────────────────────────────────────────

const TAG_CATEGORY_ORDER: Record<string, number> = { vip: 0, status: 1, custom: 2 };

/** One filter value per tag: `${category}:${value}`, so a status and a custom
 *  tag that share a word stay distinct. */
export function tagKey(t: LeadTag): string {
  return `${t.category}:${t.value}`;
}

export function tagLabel(key: string): string {
  const i = key.indexOf(':');
  const category = key.slice(0, i);
  const value = key.slice(i + 1);
  if (category === 'vip') return CRM_COPY.bulk.vipOption;
  if (category === 'status') return `Status: ${value.replace(/_/g, ' ')}`;
  return value;
}

// ── What each column sorts and filters on ──────────────────────────────────

const TEMP_RANK: Record<string, number> = { hot: 0, warm: 1, cold: 2 };

/**
 * The text each column filters on. It is what the cell SHOWS, so a pick list
 * never offers a value the operator can't see (Scanned by falls back to the
 * rep id prefix exactly as its cell does).
 */
export const LEAD_CELL_TEXT = {
  preview: (r: BatchLead) => r.preview,
  companyName: (r: BatchLead) => r.companyName,
  title: (r: BatchLead) => r.title,
  email: (r: BatchLead) => r.email,
  phone: (r: BatchLead) => r.phone,
  temperature: (r: BatchLead) => r.temperature,
  assignedTo: (r: BatchLead) => r.assignedTo,
  repName: (r: BatchLead) => r.repName ?? `${r.repOid.slice(0, 8)}…`,
  createdAt: (r: BatchLead) => localDayKey(r.createdAt),
  captureType: (r: BatchLead) => r.captureType,
  tags: (r: BatchLead) => (r.tags ?? []).map(tagKey),
  aiSummary: (r: BatchLead) => r.aiSummary,
  /** The Notes column: the latest rep note's text. */
  notes: (r: BatchLead) => r.latestNote?.text,
  deletedAt: (r: BatchLead) => r.deletedAt,
} satisfies Record<string, CellGetter>;

function pickPredicate(get: CellGetter): OnFilter {
  return (value, row) => {
    const vs = cellValues(get, row);
    return value === BLANK ? vs.length === 0 : vs.includes(String(value));
  };
}

function containsPredicate(get: CellGetter): OnFilter {
  return (value, row) => {
    const text = cellValues(get, row).join(' ');
    return value === BLANK ? text === '' : text.toLowerCase().includes(String(value).toLowerCase());
  };
}

export const COUNTS_FILTER = {
  hasOpenTasks: 'open-tasks',
  noOpenTasks: 'no-open-tasks',
  noNotes: 'no-notes',
} as const;

const countsPredicate: OnFilter = (value, row) => {
  if (value === COUNTS_FILTER.hasOpenTasks) return (row.openTaskCount ?? 0) > 0;
  if (value === COUNTS_FILTER.noOpenTasks) return (row.openTaskCount ?? 0) === 0;
  if (value === COUNTS_FILTER.noNotes) return (row.notesCount ?? 0) === 0;
  return false;
};

/** Every filterable column's predicate, keyed by column key. */
export const LEAD_FILTER_PREDICATES: Record<string, OnFilter> = {
  preview: containsPredicate(LEAD_CELL_TEXT.preview),
  email: containsPredicate(LEAD_CELL_TEXT.email),
  phone: containsPredicate(LEAD_CELL_TEXT.phone),
  notes: containsPredicate(LEAD_CELL_TEXT.notes),
  companyName: pickPredicate(LEAD_CELL_TEXT.companyName),
  title: pickPredicate(LEAD_CELL_TEXT.title),
  temperature: pickPredicate(LEAD_CELL_TEXT.temperature),
  assignedTo: pickPredicate(LEAD_CELL_TEXT.assignedTo),
  repName: pickPredicate(LEAD_CELL_TEXT.repName),
  createdAt: pickPredicate(LEAD_CELL_TEXT.createdAt),
  captureType: pickPredicate(LEAD_CELL_TEXT.captureType),
  tags: pickPredicate(LEAD_CELL_TEXT.tags),
  counts: countsPredicate,
};

export function hasActiveFilter(filters: LeadFilters): boolean {
  return Object.values(filters).some((v) => Array.isArray(v) && v.length > 0);
}

/**
 * The rows the grid shows under `filters` — antd's own rule: a row stays when,
 * for every filtered column, at least one ticked value matches it.
 *
 * `extra` adds predicates for columns this module doesn't own (a CRM lens's
 * status column), so the selection guard sees exactly what antd filters.
 */
export function filterLeads(
  rows: BatchLead[],
  filters: LeadFilters,
  extra: Record<string, OnFilter> = {},
): BatchLead[] {
  const predicateFor = (key: string): OnFilter | undefined => extra[key] ?? LEAD_FILTER_PREDICATES[key];
  const active = Object.entries(filters).filter(
    ([key, v]) => Array.isArray(v) && v.length > 0 && predicateFor(key),
  ) as [string, FilterValue][];
  if (active.length === 0) return rows;
  return rows.filter((row) =>
    active.every(([key, values]) => values.some((v) => predicateFor(key)!(v, row))),
  );
}

/**
 * A pick-list filter over a value this module doesn't know (a CRM lens's
 * status label): the distinct values present in `rows`, matched exactly.
 * Returns the column props and the predicate, which the table also hands to
 * `filterLeads`.
 */
export function valuePickFilter(
  key: string,
  rows: BatchLead[],
  get: (row: BatchLead) => string,
  filters: LeadFilters,
): { props: Partial<Col>; predicate: OnFilter } {
  const values = [...new Set(rows.map(get).filter(Boolean))].sort((a, b) => collator.compare(a, b));
  const predicate: OnFilter = (value, row) => get(row) === String(value);
  return {
    props: {
      filters: values.map((value) => ({ text: value, value })),
      filterSearch: values.length > 6,
      filterMultiple: true,
      onFilter: predicate,
      filteredValue: filters[key] ?? null,
    },
    predicate,
  };
}

// ── Sorters ────────────────────────────────────────────────────────────────

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * Blanks go last in BOTH directions, as in a spreadsheet: sorting Company
 * Z → A should show companies, not every blank row first. antd negates a
 * comparator's result for a descending sort, so the blank case is
 * pre-negated here to come out last either way.
 */
function blanksLast(aBlank: boolean, bBlank: boolean, order: SortOrder | undefined): number {
  if (aBlank === bBlank) return 0;
  const last = aBlank ? 1 : -1;
  return order === 'descend' ? -last : last;
}

/** A → Z, case-insensitive, numbers in numeric order, blanks last. */
export function textSorter(get: CellGetter): CompareFn<BatchLead> {
  return (a, b, order) => {
    const x = cellValues(get, a).join(', ');
    const y = cellValues(get, b).join(', ');
    if (!x || !y) return blanksLast(!x, !y, order);
    return collator.compare(x, y);
  };
}

/** Hot → warm → cold (any other value after cold), blanks last. */
export const temperatureSorter: CompareFn<BatchLead> = (a, b, order) => {
  const x = a.temperature?.trim() ? (TEMP_RANK[a.temperature.trim()] ?? 3) : null;
  const y = b.temperature?.trim() ? (TEMP_RANK[b.temperature.trim()] ?? 3) : null;
  if (x === null || y === null) return blanksLast(x === null, y === null, order);
  return x - y;
};

/** Note count, then open tasks. */
export const countsSorter: CompareFn<BatchLead> = (a, b) =>
  (a.notesCount ?? 0) - (b.notesCount ?? 0) || (a.openTaskCount ?? 0) - (b.openTaskCount ?? 0);

// ── Pick-list options ──────────────────────────────────────────────────────

export interface PickOption {
  text: string;
  value: string;
}

interface PickOptionsConfig {
  label?: (value: string) => string;
  /** Orders the values; defaults to their labels, A → Z. */
  order?: (a: string, b: string) => number;
}

/** Distinct values present in `rows`, then "(Blank)" if any row is blank. */
export function pickOptions(
  rows: BatchLead[],
  get: CellGetter,
  { label = (v) => v, order }: PickOptionsConfig = {},
): PickOption[] {
  const seen = new Set<string>();
  let hasBlank = false;
  for (const row of rows) {
    const vs = cellValues(get, row);
    if (vs.length === 0) hasBlank = true;
    for (const v of vs) seen.add(v);
  }
  const values = [...seen].sort(order ?? ((a, b) => collator.compare(label(a), label(b))));
  const options = values.map((value) => ({ text: label(value), value }));
  if (hasBlank) options.push({ text: GRID_COPY.blankOption, value: BLANK });
  return options;
}

/** Captured: one option per local day, oldest first. */
export function capturedDayOptions(rows: BatchLead[]): PickOption[] {
  const days = new Set(rows.map((r) => localDayKey(r.createdAt)).filter(Boolean) as string[]);
  const withYear = new Set([...days].map((d) => d.slice(0, 4))).size > 1;
  return pickOptions(rows, LEAD_CELL_TEXT.createdAt, {
    label: (d) => dayLabel(d, withYear),
    order: (a, b) => a.localeCompare(b),
  });
}

/** Temp: hot, warm, cold, then anything else A → Z. */
export function temperatureOptions(rows: BatchLead[]): PickOption[] {
  return pickOptions(rows, LEAD_CELL_TEXT.temperature, {
    order: (a, b) => (TEMP_RANK[a] ?? 3) - (TEMP_RANK[b] ?? 3) || collator.compare(a, b),
  });
}

/** Tags: VIP, then statuses, then custom tags, each A → Z. */
export function tagOptions(rows: BatchLead[]): PickOption[] {
  return pickOptions(rows, LEAD_CELL_TEXT.tags, {
    label: tagLabel,
    order: (a, b) =>
      (TAG_CATEGORY_ORDER[a.slice(0, a.indexOf(':'))] ?? 3) -
        (TAG_CATEGORY_ORDER[b.slice(0, b.indexOf(':'))] ?? 3) ||
      collator.compare(tagLabel(a), tagLabel(b)),
  });
}

// ── Column props ───────────────────────────────────────────────────────────

type Col = TableColumnType<BatchLead>;

/** Pick-list filter props for column `key`. A search box appears once the
 *  list runs past six entries. */
export function pickFilterProps(key: string, options: PickOption[], filters: LeadFilters): Partial<Col> {
  return {
    filters: options,
    filterSearch: options.length > 6,
    filterMultiple: true,
    onFilter: LEAD_FILTER_PREDICATES[key],
    filteredValue: filters[key] ?? null,
  };
}

/** "Contains…" + "Only blank" filter props for column `key`. */
export function searchFilterProps(key: string, filters: LeadFilters, activeColor: string): Partial<Col> {
  return {
    // createElement rather than JSX keeps this module plain TypeScript (no
    // components of its own), which is what React Fast Refresh wants.
    filterDropdown: (props) => createElement(SearchFilterDropdown, props),
    filterIcon: (filtered) =>
      createElement(SearchOutlined, { style: { color: filtered ? activeColor : undefined } }),
    onFilter: LEAD_FILTER_PREDICATES[key],
    filteredValue: filters[key] ?? null,
  };
}

/** Notes / Tasks: Has open tasks · No open tasks · No notes. */
export function countsFilterProps(filters: LeadFilters): Partial<Col> {
  return {
    filters: [
      { text: GRID_COPY.hasOpenTasks, value: COUNTS_FILTER.hasOpenTasks },
      { text: GRID_COPY.noOpenTasks, value: COUNTS_FILTER.noOpenTasks },
      { text: GRID_COPY.noNotes, value: COUNTS_FILTER.noNotes },
    ],
    filterMultiple: true,
    onFilter: LEAD_FILTER_PREDICATES.counts,
    filteredValue: filters.counts ?? null,
  };
}

/** Controlled sort props for column `key`. */
export function sortProps(key: string, sorter: CompareFn<BatchLead>, sort: LeadSort | null): Partial<Col> {
  return { sorter, sortOrder: sort?.columnKey === key ? sort.order : null };
}
